"use client";

import { medimoQC } from "@medimo/core-api/internal/next";
import {
  StyledEditor,
  extensionsWithLink,
  useEditor,
} from "@medimo/shared-editor";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@medimo/ui";
import { ChevronRight, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useInvalidateUnreadStatus } from "../hooks/use-unread-status";
import {
  clearPendingClosedEventFor,
  sendClosedEvent,
} from "../lib/closed-event";

const MODAL_VIEW_DURATION_MS_MAX = 86_400_000;

export type ReleaseNotification = {
  id: string;
  title: string;
  body: string;
  publish_at: number;
  kind: string;
  image_url: string | null;
  image_alt_text: string | null;
};

const formatReleaseDate = (publishAt: number) =>
  new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(new Date(publishAt * 1000));

// リリース通知モーダル（Figma: リリース通知_モーダル）
export const ReleaseNotificationDialog = ({
  notification,
  loginAttemptId,
}: {
  notification: ReleaseNotification;
  loginAttemptId: string;
}) => {
  const [open, setOpen] = useState(true);
  const [readError, setReadError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imageRetriedRef = useRef(false);
  const openedAtRef = useRef<number | null>(null);
  const closedSentRef = useRef(false);
  const [imageUrl, setImageUrl] = useState(notification.image_url);
  const invalidateUnreadStatus = useInvalidateUnreadStatus();

  const { mutateAsync: markRead, isPending: isReadPending } =
    medimoQC.useMutation("post", "/v2/notifications/{notification_id}/read");
  const { mutateAsync: reclaim } = medimoQC.useMutation(
    "post",
    "/v2/notifications/popup/claim",
  );

  const bodyEditor = useEditor({
    editable: false,
    extensions: extensionsWithLink,
    immediatelyRender: false,
    content: "",
  });
  useEffect(() => {
    openedAtRef.current = performance.now();
  }, []);

  useEffect(() => {
    if (bodyEditor && notification.body) {
      bodyEditor.commands.setContent(notification.body);
    }
  }, [bodyEditor, notification.body]);

  const sendClosed = (action: "later" | "close") => {
    if (closedSentRef.current) return;
    closedSentRef.current = true;
    const openedAt = openedAtRef.current ?? performance.now();
    const viewDurationMs = Math.min(
      Math.max(Math.round(performance.now() - openedAt), 0),
      MODAL_VIEW_DURATION_MS_MAX,
    );
    // 未送信のまま失われると3秒未満クローズが記録されず2回目が永久に出なくなるため、
    // keepalive で送りつつ、応答を得られなければ次回起動時に同じIDで再送する
    sendClosedEvent({
      notificationId: notification.id,
      loginAttemptId,
      viewDurationMs,
      action,
    });
  };

  const onOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      sendClosed("close");
    }
    setOpen(nextOpen);
  };

  const onClickLater = () => {
    sendClosed("later");
    setOpen(false);
  };

  const onClickConfirm = async () => {
    setReadError(false);
    try {
      await markRead({
        params: { path: { notification_id: notification.id } },
      });
      closedSentRef.current = true;
      clearPendingClosedEventFor(notification.id);
      invalidateUnreadStatus();
      setOpen(false);
    } catch (e) {
      console.error("リリース通知の既読化に失敗しました:", e);
      setReadError(true);
    }
  };

  // 一覧を見る場合は read を送らず遷移する（一覧から記事を開いた時点で既読になる）
  const onClickViewList = () => {
    sendClosed("close");
    setOpen(false);
  };

  const onImageLoadError = async () => {
    // presigned URL の失効時は 1 回だけ再取得し、再失敗時は代替テキスト表示に切り替える
    if (imageRetriedRef.current) {
      setImageError(true);
      return;
    }
    imageRetriedRef.current = true;
    try {
      const refreshed = await reclaim({
        body: { login_attempt_id: loginAttemptId },
      });
      if (refreshed.notification?.image_url) {
        setImageUrl(refreshed.notification.image_url);
        return;
      }
    } catch (e) {
      console.error("画像URLの再取得に失敗しました:", e);
    }
    setImageError(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/[0.45]"
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[600px] flex-col gap-0 overflow-hidden rounded-[13px] border-0 p-0 shadow-lg sm:max-h-[calc(100dvh-10rem)] sm:rounded-[13px]"
      >
        <DialogHeader className="shrink-0 gap-3 space-y-0 overflow-hidden px-6 pt-4 pb-3">
          <div className="flex h-8 items-center justify-between">
            <span className="flex h-8 min-w-[72px] items-center justify-center rounded border border-primary px-2 font-bold text-primary text-sm leading-[1.3]">
              新機能
            </span>
            <DialogClose asChild>
              <button
                type="button"
                className="flex size-6 shrink-0 items-center justify-center text-[#1d2433] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="閉じる"
              >
                <X className="size-6" strokeWidth={1.5} />
              </button>
            </DialogClose>
          </div>
          <DialogTitle className="text-left font-bold text-[#1d2433] text-xl leading-8 tracking-[0.02em]">
            {notification.title}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            {imageUrl && !imageError ? (
              // 画像はモーダル幅に合わせ、縦はアスペクト比に応じて可変（DD-2026-0818 §3）
              <img
                src={imageUrl}
                alt={notification.image_alt_text ?? ""}
                className="h-auto w-full rounded-lg"
                onError={onImageLoadError}
              />
            ) : null}
            {imageError ? (
              <div className="rounded-lg border bg-gray-50 p-4 text-gray-500 text-sm">
                画像を読み込めませんでした
                {notification.image_alt_text
                  ? `（${notification.image_alt_text}）`
                  : ""}
              </div>
            ) : null}
            <div className="whitespace-pre-wrap">
              <StyledEditor
                className="notification-text release-notification-text text-[#111928] text-base leading-[1.6] tracking-[0.02em] [&_a]:text-[#006f83] [&_a]:underline"
                editor={bodyEditor}
                disabled
              />
            </div>
          </div>
        </DialogDescription>
        {readError ? (
          <p className="px-6 py-2 text-red-500 text-sm">
            既読状態を更新できませんでした。通信環境をご確認のうえ、もう一度お試しください。
          </p>
        ) : null}
        <div className="grid shrink-0 grid-cols-2 gap-3 border-[#d1d5db] border-t px-6 py-2 sm:grid-cols-[minmax(0,3fr)_minmax(0,7fr)]">
          <Button
            variant="outline"
            className="h-12 w-full rounded-lg border-[#d1d5db] px-3 font-semibold text-[#111928] leading-none sm:px-4"
            onClick={onClickLater}
            disabled={isReadPending}
          >
            あとで見る
          </Button>
          <Button
            className="h-12 w-full rounded-lg px-3 font-semibold text-white leading-none sm:px-4"
            onClick={onClickConfirm}
            disabled={isReadPending}
          >
            わかりました
          </Button>
        </div>
        <div className="flex shrink-0 items-center justify-between px-6 pt-3 pb-4 text-gray-500 text-sm leading-[1.3]">
          <span>{formatReleaseDate(notification.publish_at)}</span>
          <Link
            href="/notifications"
            className="flex items-center hover:underline"
            onClick={onClickViewList}
          >
            お知らせ一覧を見る
            <ChevronRight size={16} />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};
